import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatAPI } from '../api/chat';
import './ChatWidget.css';

// ── Language detection (same logic as LandingPage) ──────────────────────────
const IS_PERSIAN =
  window.location.hostname === 'potterhead.ir' ||
  window.location.hostname === 'www.potterhead.ir';

const CHAT_TEXT = {
  fa: {
    dir: 'rtl',
    greetings: [
      'آه، سلام بر تو، جوان جادوگر! من آلبوس دامبلدور هستم. در «جامعه پاترهدها» چه پرسشی بر ذهن تو سنگینی می‌کند؟',
      'خوش آمدی! من پروفسور دامبلدور هستم. بفرما بنشین و هر آنچه درباره‌ی هاگوارتز و پاترهدها می‌خواهی بدانی، بپرس.',
      'سلام دوست جوانم. جادو همیشه با یک پرسش خوب آغاز می‌شود؛ پرسش تو چیست؟',
      'چه خوب که آمدی. من اینجا هستم تا در مسیر پاترهدها راهنمایت کنم — از چه بگوییم؟',
      'درود بر تو! کنجکاوی، آغاز هر جادویی است. چه چیزی می‌خواهی بدانی؟',
    ],
    title:       'پروفسور دامبلدور',
    subtitle:    'جامعه پاترهدها',
    fabTitle:    'گفتگو با دامبلدور',
    typing:      'دامبلدور با پر مشغول نوشتن است...',
    placeholder: 'پیام خود را بنویسید...',
    send:        'ارسال',
    close:       'بستن',
    suggestions: [
      'جامعه پاترهدها چیست؟',
      'چطور در آزمون‌های پذیرش شرکت کنم؟',
      'خانه‌ی من چطور مشخص می‌شود؟',
      'قطار هاگوارتز چیست و چطور سوارش شوم؟',
    ],
    fallback:
      'فرزند عزیزم، گاهی حتی جادوهای بزرگ هم به کمی سکوت نیاز دارند. من اکنون نمی‌توانم پاسخ دقیقی بدهم؛ کمی بعد دوباره بپرس و بدان که در هاگوارتز همیشه نوری برای راهنمایی تو هست.',
  },
  en: {
    dir: 'ltr',
    greetings: [
      "Ah, greetings, young witch or wizard! I am Albus Dumbledore. What question weighs upon your mind in the Potterheads Community?",
      "Welcome, welcome. I am Professor Dumbledore — sit a while, and ask me anything about Hogwarts or the Potterheads Community.",
      "Hello, young friend. Every bit of magic begins with a good question — what's yours?",
      "How lovely that you've come. I'm here to guide you through the Potterheads journey — what shall we discuss?",
      "Greetings! Curiosity is where all magic begins. What would you like to know?",
    ],
    title:       'Professor Dumbledore',
    subtitle:    'Potterheads Community',
    fabTitle:    'Chat with Dumbledore',
    typing:      'Dumbledore is typing',
    placeholder: 'Write your message...',
    send:        'Send',
    close:       'Close',
    suggestions: [
      'What is the Potterheads Community?',
      'How do I take the admission tests?',
      'How is my house decided?',
      'What is the Hogwarts Express and how do I board it?',
    ],
    fallback:
      'My dear child, even the greatest magic sometimes needs a moment of silence. I cannot offer a precise answer right now; ask me again shortly, and remember that at Hogwarts there is always a light to guide you.',
  },
};

const ct = IS_PERSIAN ? CHAT_TEXT.fa : CHAT_TEXT.en;

function randomGreeting() {
  const list = ct.greetings;
  return list[Math.floor(Math.random() * list.length)];
}

export default function ChatWidget() {
  const [open, setOpen]               = useState(false);
  const [config, setConfig]           = useState(null);
  const [messages, setMessages]       = useState(() => [{ role: 'assistant', content: randomGreeting() }]);
  const [input, setInput]             = useState('');
  const [conversationId, setConversationId] = useState(
    () => localStorage.getItem('chat_last_conversation') || null
  );
  const [typing, setTyping]           = useState(false);
  const messagesRef                   = useRef(null);
  const textareaRef                   = useRef(null);

  useEffect(() => {
    chatAPI.getConfig()
      .then((res) => setConfig(res.data.config))
      .catch(() => setConfig({ enabled: true }));
  }, []);

  // Restore previous conversation when opening
  useEffect(() => {
    if (open && conversationId) {
      chatAPI.getConversation(conversationId)
        .then((res) => {
          const msgs = res.data.conversation?.messages || [];
          if (msgs.length) {
            setMessages(msgs.map((m) => ({ role: m.role, content: m.content })));
          }
        })
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(scrollToBottom, [messages, typing, scrollToBottom]);

  // Auto-grow the textarea as the user types, up to a max height (CSS caps it).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || typing) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setTyping(true);

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    await chatAPI.streamMessage(text, conversationId, config?.defaultModel, {
      language: IS_PERSIAN ? 'fa' : 'en',
      onMeta: (meta) => {
        if (meta.conversationId) {
          setConversationId(meta.conversationId);
          localStorage.setItem('chat_last_conversation', meta.conversationId);
        }
      },
      onDelta: (delta) => {
        setTyping(false);
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, content: last.content + delta };
          }
          return copy;
        });
      },
      onDone: () => setTyping(false),
      onError: () => {
        setTyping(false);
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            copy[copy.length - 1] = { ...last, content: ct.fallback };
          } else {
            copy.push({ role: 'assistant', content: ct.fallback });
          }
          return copy;
        });
      }
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  // Hide only when config is explicitly loaded and enabled=false
  if (config !== null && config.enabled === false) return null;

  const panelClass = `dumbledore-panel${IS_PERSIAN ? '' : ' ltr'}`;
  const showSuggestions = !typing && !messages.some((m) => m.role === 'user');

  return (
    <>
      {!open && (
        <button className="dumbledore-fab" onClick={() => setOpen(true)} title={ct.fabTitle} style={{ backgroundImage: "url('/dumbledore.jpg')" }} />
      )}

      {open && (
        <div className={panelClass} dir={ct.dir}>
          <div className="dumbledore-header">
            <div className="dumbledore-header-profile">
              <img className="dumbledore-header-avatar" src="/dumbledore.jpg" alt="Dumbledore" />
              <div className="dumbledore-header-info">
                <div className="title">{ct.title}</div>
                <div className="subtitle">{ct.subtitle}</div>
              </div>
            </div>
            <button className="dumbledore-header-close" onClick={() => setOpen(false)} title={ct.close}>✕</button>
          </div>

          <div className="dumbledore-messages" ref={messagesRef}>
            {messages.map((m, i) => (
              <div key={i} className={`dumbledore-bubble-container ${m.role}`}>
                {m.role === 'assistant' && (
                  <img className="dumbledore-bubble-avatar" src="/dumbledore.jpg" alt="Dumbledore" />
                )}
                <div className={`dumbledore-bubble ${m.role}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {typing && (
              <div className="dumbledore-bubble-container assistant">
                <img className="dumbledore-bubble-avatar" src="/dumbledore.jpg" alt="Dumbledore" />
                <div className="dumbledore-typing-container">
                  <span className="dumbledore-typing-label">{ct.typing}</span>
                  <div className="dumbledore-typing-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showSuggestions && (
            <div className="dumbledore-suggestions">
              {ct.suggestions.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  className="dumbledore-chip"
                  onClick={() => send(q)}
                  disabled={typing}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="dumbledore-input">
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder={ct.placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <button
              className="dumbledore-send-btn"
              onClick={() => send()}
              disabled={typing || !input.trim()}
              title={ct.send}
              aria-label={ct.send}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M2.5 21.5l19-9.5-19-9.5v7.4l13 2.1-13 2.1z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
